import { useMemo, useState } from 'react';
import { CheckIcon, Popover, PopoverContent, PopoverTrigger, cn } from '@playerbase/ui';
import type { BuildMarker, GameDevStage } from '@playerbase/types';

import { StageBadge } from './stage-badge.js';
import type { BuildMarkerFilter } from '../../games/playtest-sessions-data.js';
import type { BuildStageSummary } from '../../games/playtest-sessions-data.js';

interface Props {
  value: BuildMarkerFilter;
  onChange: (next: BuildMarkerFilter) => void;
  builds: BuildMarker[];
  stages: GameDevStage[];
}

// Rich build picker for the session filter bar. Mirrors how builds
// appear elsewhere in the portal (label + version + stage badge) so
// studios can pick the same build by sight from any surface.
export function BuildPicker({ value, onChange, builds, stages }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const stageById = useMemo(() => {
    const m = new Map<string, BuildStageSummary>();
    for (const s of stages) {
      m.set(s.id, { id: s.id, label: s.label, colour: s.colour });
    }
    return m;
  }, [stages]);

  const selectedBuild =
    value !== null && value !== 'unattributed'
      ? (builds.find((b) => b.id === value) ?? null)
      : null;

  function select(next: BuildMarkerFilter): void {
    onChange(next);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filter by build"
          className="flex min-w-[14rem] items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 py-1 text-left text-sm text-text hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="flex min-w-0 items-center gap-2">
            {value === null ? (
              <span className="text-text-muted">Any</span>
            ) : value === 'unattributed' ? (
              <span>Unattributed</span>
            ) : selectedBuild === null ? (
              <span className="text-text-muted">Unknown build</span>
            ) : (
              <BuildLine build={selectedBuild} stage={stageById.get(selectedBuild.stageId) ?? null} />
            )}
          </span>
          <span aria-hidden className="text-text-muted">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-72 min-w-[20rem] overflow-y-auto p-1">
        <Option active={value === null} onSelect={() => select(null)}>
          <span>Any</span>
        </Option>
        <Option
          active={value === 'unattributed'}
          onSelect={() => select('unattributed')}
        >
          <span>Unattributed</span>
        </Option>
        {builds.length > 0 ? (
          <div className="my-1 border-t border-border" aria-hidden />
        ) : null}
        {builds.map((b) => (
          <Option
            key={b.id}
            active={value === b.id}
            onSelect={() => select(b.id)}
          >
            <BuildLine build={b} stage={stageById.get(b.stageId) ?? null} />
          </Option>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function Option({
  active,
  onSelect,
  children,
}: {
  active: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-text hover:bg-surface-3',
        active && 'bg-surface-3',
      )}
    >
      {children}
      {active ? <CheckIcon className="text-accent" /> : null}
    </button>
  );
}

function BuildLine({
  build,
  stage,
}: {
  build: BuildMarker;
  stage: BuildStageSummary | null;
}): JSX.Element {
  const compact = build.label === build.version
    ? build.label
    : `${build.label} (${build.version})`;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate text-text">{compact}</span>
      {stage !== null ? <StageBadge stage={stage} /> : null}
    </span>
  );
}
